/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import CampaignModel from 'cio/models/campaign';
import { Periods, MEASURE_LABELS, Measure, MEASURES, Channel } from 'cio/utils/metrics/common';
import { DateRange } from '@customerio/fly-components/utils/fly-date-picker-utils';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import ActionModel, { ActionType, VariationBadge } from 'cio/models/action';
import actionInfo, { actionTypeName, deliveryStatesAndActionsPerType } from 'cio/utils/action-info';
import { METRIC_COLORS } from 'cio/utils/metrics/colors';
import MetricsService from 'cio/services/metrics';
import { Metric } from 'cio/utils/metrics/models/metric';
import { Grouping } from 'cio/utils/metrics/resolution';
import { DataContainer, prepareDataContainer, SidebarItem } from 'cio/utils/campaign-overview/metrics';
import { findDenominator, MeasureType } from 'cio/utils/metrics/measure';
import DS from 'ember-data';
import AnalyticsService from 'cio/services/analytics';
import { EditType } from 'cio/utils/analytics/transforms/campaign';
import SettingsService from 'cio/services/settings';

interface Args {
  campaign: CampaignModel;
  timeRange: DateRange;
  timePeriod: Periods;
  grouping: Grouping;
  unit: 'count' | 'percentage';
  measures: Measure[];
  onMeasuresChange: (measures: Measure[]) => unknown;
  individualMeasure: Measure;
  onIndividualMeasureChange: (measure: Measure) => unknown;
  isLoadingMetrics: boolean;
  onTrackCopyLink: () => unknown;

  chartType: 'individual' | 'aggregate';
  onChartTypeChange: (type: 'individual' | 'aggregate') => unknown;
  channelMetrics: Partial<Record<Channel, Metric>>;
  actionMetrics: Record<string, Metric>;
  channel: Channel;
  onChannelChange: (channel: Channel) => unknown;
}

export default class DeliveryMetricsTab extends Component<Args> {
  @service analytics!: AnalyticsService;
  @service metrics!: MetricsService;
  @service settings!: SettingsService;
  @service store!: DS.Store;
  @service environment!: EnvironmentService;

  get selectedChannelOption() {
    return this.availableChannelOptions.find((i) => i.value === this.args.channel);
  }

  @action
  private trackChange(editType: EditType) {
    let selectedMetrics: string[] = [];
    let messagesSelectedCount = 0;

    if (this.args.chartType === 'individual') {
      selectedMetrics = [this.args.individualMeasure];
      messagesSelectedCount = this.selectedAvailableActionValues.length;
    } else {
      selectedMetrics = this.args.measures;
      messagesSelectedCount = 0;
    }

    this.analytics.track('campaign', 'performance_delivery_metrics_chart_changed', {
      model: this.args.campaign,
      selectedMetrics,
      messagesSelectedCount,
      channel: this.args.channel,
      chartType: this.args.chartType,
      editType,
    });
  }

  @action
  private setDefaultIndividualActionItems() {
    if (this.args.chartType !== 'individual') {
      return;
    }

    // if there aren't any enabled actions, try to enable the first 7 to avoid an empty chart
    if (!this.selectedAvailableActionValues.length && this.sidebarItems.length) {
      this.selectedActionValues = this.sidebarItems.slice(0, 7).map((i) => i.id);
    }
  }

  @action
  onChartTypeChange(chartType: 'individual' | 'aggregate') {
    this.args.onChartTypeChange(chartType);

    this.setDataContainer();
    this.setDefaultIndividualActionItems();

    this.trackChange(EditType.ChartType);
  }

  availableChannelOptions = this.environment.availableChannelsForCampaign(this.args.campaign).map((typeName) => {
    let info = actionInfo(`${typeName}_action` as ActionType);

    return {
      label: info.name,
      value: typeName,
      icon: info.icon,
    };
  });

  @action
  onChannelSelect(channel: Channel) {
    this.args.onChannelChange(channel);
    this.args.onMeasuresChange(this.args.measures.filter((m) => this.availableSidebarMeasureValues.includes(m)));

    this.setDataContainer();
    this.setDefaultIndividualActionItems();

    this.trackChange(EditType.Channel);
  }

  get sidebarMeasures() {
    let actionType = `${this.args.channel}_action` as ActionType;
    let availableMeasures = deliveryStatesAndActionsPerType(this.availableActions.some((a) => a.canConvert))[
      actionType
    ];

    return [
      'sent',
      'delivered',
      'opened',
      'clicked',
      'clicked_to_opened',
      'converted',
      'unsubscribed',
      'topic_unsubscribed',
      'spammed',
      'failed',
      'bounced',
      'suppressed',
    ]
      .filter((m) => availableMeasures.includes(m))
      .map((measure: Measure, i: number) => {
        let denominator = findDenominator(measure, MeasureType.DeliveryMetrics, actionType);

        return {
          id: measure,
          label: MEASURE_LABELS[measure],
          value: measure,
          color: METRIC_COLORS[i % METRIC_COLORS.length],
          hasRate: denominator != null,
          measure,
          denominatorMeasure: denominator,
          index: i,
        };
      });
  }

  get availableSidebarMeasureValues() {
    return this.sidebarMeasures.map((m) => m.id);
  }

  get sidebarActions() {
    let { actionMetrics } = this;

    return actionMetrics.map((a, i) => {
      let badge: VariationBadge | undefined;
      let denominator = findDenominator(this.args.individualMeasure, MeasureType.DeliveryMetrics, a.action.type);

      if (a.action.isVariation) {
        let parentAction = a.action.belongsTo('parentAction').value() as ActionModel;
        badge = parentAction.splitActions.find((sa) => sa.action.id === a.action.id)?.badge;
      }

      return {
        id: a.action.id,
        value: a.action.id,
        label: a.action.name,
        color: METRIC_COLORS[i % METRIC_COLORS.length],
        hasRate: denominator != null,
        measure: this.args.individualMeasure,
        denominatorMeasure: denominator,
        action: a.action,
        metric: a.metric,
        badge,
        index: i,
      };
    });
  }

  get sidebarItems(): SidebarItem<Measure>[] {
    if (this.args.chartType === 'aggregate') {
      return this.sidebarMeasures;
    }

    if (this.args.chartType === 'individual') {
      return this.sidebarActions;
    }

    return [];
  }

  get selectedSidebarValues() {
    if (this.args.chartType === 'aggregate') {
      return this.args.measures;
    }

    if (this.args.chartType === 'individual') {
      return this.selectedActionValues;
    }

    return [];
  }

  @tracked selectedActionValues: string[] = this.availableActions.slice(0, 7).map((a) => a.id);

  get selectedAvailableActionValues() {
    return this.sidebarActions.filter((a) => this.selectedActionValues.includes(a.id)).map((a) => a.id);
  }

  @action
  onSelectSidebarItem(itemId: string, checked: boolean) {
    if (this.args.chartType === 'aggregate') {
      if (checked) {
        this.args.onMeasuresChange([...this.args.measures, itemId as Measure]);
      } else {
        this.args.onMeasuresChange(this.args.measures.filter((m) => m !== itemId));
      }
    } else {
      if (checked) {
        this.selectedActionValues = [...this.selectedActionValues, itemId];
      } else {
        this.selectedActionValues = this.selectedActionValues.filter((m) => m !== itemId);
      }
    }

    this.trackChange(EditType.SelectedMetrics);
  }

  get availableActions() {
    let { messagingActions } = this.args.campaign;
    let selectedActionType = `${this.args.channel}_action`;

    return messagingActions.reduce<ActionModel[]>((acc, a) => {
      if (a.isSplitTest && selectedActionType === 'email_action') {
        let children = a.splitActions.map((a) => a.action).filter((a) => a.type === selectedActionType);
        return [...acc, a, ...children];
      }

      if (a.type !== selectedActionType) {
        return acc;
      }

      return [...acc, a];
    }, [] as ActionModel[]);
  }

  @action
  onIndividualMeasureChange(value: Measure) {
    this.args.onIndividualMeasureChange(value);

    this.trackChange(EditType.IndividualMetric);
  }

  @tracked channelMetric: Metric | null = null;

  @action
  setChannelMetric() {
    this.channelMetric = this.args.channelMetrics[this.args.channel] ?? null;
  }

  @tracked actionMetrics: { action: ActionModel; metric: Metric }[] = [];

  @action
  setActionMetrics() {
    this.actionMetrics = Object.keys(this.args.actionMetrics)
      .reduce<{ action: ActionModel; metric: Metric }[]>((acc, actionId) => {
        let metric = this.args.actionMetrics[actionId];

        let type = metric.actionType === 'split_randomized_action' ? 'email_action' : metric.actionType;
        let actionChannel = actionTypeName(type as ActionType);

        if (actionChannel !== this.args.channel) {
          return acc;
        }

        return [...acc, { action: this.store.peekRecord('action', metric.entityId) as ActionModel, metric }];
      }, [])
      .sort((a, b) => {
        // If one item is a split action, and the other is one of its
        // variations, sort the parent split higher
        if (a.action.isSplitTest && b.action.isVariation && b.action.belongsTo('parentAction').id() === a.action.id) {
          return 1;
        }

        if (b.action.isSplitTest && a.action.isVariation && a.action.belongsTo('parentAction').id() === b.action.id) {
          return 1;
        }

        // If both actions are split variations and belong to the same parent,
        // sort the original first, then the variation
        if (
          a.action.isVariation &&
          b.action.isVariation &&
          a.action.belongsTo('parentAction').id() === b.action.belongsTo('parentAction').id()
        ) {
          let idOrder = (a.action.belongsTo('parentAction').value() as ActionModel).splitActions.map(
            (a) => a.action.id,
          );
          return idOrder.indexOf(a.action.id) - idOrder.indexOf(b.action.id);
        }

        return a.action.name.localeCompare(b.action.name, undefined, { sensitivity: 'accent' });
      });
  }

  @tracked aggregateChartData: DataContainer<Measure> = {} as any;
  @tracked individualChartData: Record<string, DataContainer<Measure>> = {} as any;

  @tracked isLoading = true;

  @action
  setDataContainer() {
    if (this.args.isLoadingMetrics) {
      return;
    }

    if (this.args.chartType === 'aggregate') {
      this.setChannelMetric();
    } else {
      this.setActionMetrics();
    }

    if (this.channelMetric == null && this.actionMetrics == null) {
      return;
    }

    if (this.channelMetric) {
      this.aggregateChartData = prepareDataContainer(this.channelMetric, MEASURES);
    }

    this.individualChartData = this.actionMetrics.reduce<Record<string, DataContainer<Measure>>>((acc, am) => {
      acc[am.action.id] = prepareDataContainer(am.metric, MEASURES);
      return acc;
    }, {} as any);

    if (this.isLoading) {
      this.isLoading = false;
    }
  }

  @action
  onTrackHover(time: number) {
    let selectedMetrics: string[] = [];
    let messagesSelectedCount = 0;

    if (this.args.chartType === 'individual') {
      selectedMetrics = [this.args.individualMeasure];
      messagesSelectedCount = this.selectedAvailableActionValues.length;
    } else {
      selectedMetrics = this.args.measures;
      messagesSelectedCount = 0;
    }

    this.analytics.track('campaign', 'performance_delivery_metrics_chart_hover', {
      model: this.args.campaign,
      time,
      selectedMetrics,
      messagesSelectedCount,
      measureType: MeasureType.DeliveryMetrics,
      channel: this.args.channel,
      chartType: this.args.chartType,
    });
  }
}
